/**
 * Company Types for MEV-Med System
 */

// ==================== ENUMS ====================

// Company status enumeration (English - aligned with backend)
export enum CompanyStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  DEFAULTING = 'DEFAULTING',
  BLOCKED = 'BLOCKED'
}

// Company type enumeration (English - aligned with backend)
export enum CompanyType {
  HEADQUARTERS = 'HEADQUARTERS',
  BRANCH = 'BRANCH'
}

// Company size (aligned with backend); absence (null) means "nenhum desses"
export enum CompanySize {
  MEI = 'MEI',
  ME = 'ME',
  EPP = 'EPP'
}

// Document type enumeration
export enum DocumentType {
  CNPJ = 'CNPJ',
  CAEPF = 'CAEPF',
  CNO = 'CNO'
}

// eSocial send mode enumeration
export enum ESocialSendMode {
  DONT_SEND = 'DONT_SEND',
  XML_ONLY = 'XML_ONLY',
  USE_CERTIFICATE = 'USE_CERTIFICATE'
}

// Responsible type enumeration
export enum ResponsibleType {
  CLINIC_PROVIDER = 'CLINIC_PROVIDER',
  LAB_PROVIDER = 'LAB_PROVIDER',
  ENVIRONMENT_PROVIDER = 'ENVIRONMENT_PROVIDER',
  MEDICAL_RESPONSIBLE = 'MEDICAL_RESPONSIBLE',
  ENVIRONMENTAL_RESPONSIBLE = 'ENVIRONMENTAL_RESPONSIBLE',
  TECHNICAL_RESPONSIBLE = 'TECHNICAL_RESPONSIBLE',
  LEGAL_RESPONSIBLE = 'LEGAL_RESPONSIBLE',
  SPEECH_THERAPY_RESPONSIBLE = 'SPEECH_THERAPY_RESPONSIBLE'
}

// Contact type enumeration
export enum ContactType {
  ADMINISTRATIVE = 'ADMINISTRATIVE',
  ACCOUNTING = 'ACCOUNTING',
  FINANCIAL = 'FINANCIAL',
  COMMERCIAL = 'COMMERCIAL',
  HR = 'HR'
}

// Module type enum (aligned with backend)
export enum ModuleType {
  MEDICINE = 'medicine',
  SAFETY = 'safety',
  ENVIRONMENT = 'environment',
  ESOCIAL = 'esocial'
}

// ==================== INTERFACES ====================

// Company module data (returned by backend from CompanyModules table)
export interface CompanyModuleData {
  id: number;
  companyId: number;
  module: ModuleType;
  createdAt: string;
}

// Company responsible data
export interface CompanyResponsible {
  id: number;
  companyId: number;
  responsibleType: ResponsibleType;
  providerId?: number | null;
  professionalId?: number | null;
  startDate: string;
  endDate?: string;
  notes?: string;
  provider?: { id: number; legalName: string; tradeName?: string } | null;
  professional?: { id: number; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

// Company-Provider link data (N-N clinic relationship)
export interface CompanyProviderLink {
  id: number;
  companyId: number;
  providerId: number;
  startDate: string;
  endDate?: string | null;
  notes?: string | null;
  provider?: { id: number; tradeName: string; legalName: string; providerType?: string; serviceType?: string };
  createdAt?: string;
  updatedAt?: string;
}

// Company contact data
export interface CompanyContact {
  id: number;
  companyId: number;
  contactType: ContactType;
  name: string;
  email?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Company CNAE data
export interface CompanyCnae {
  id: number;
  companyId: number;
  cnaeCode: string;
  description: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

// Base company interface (aligned with backend Company table)
export interface Company {
  id: string;
  legalName: string;
  tradeName?: string;
  cnpj: string;

  // Address (backend fields)
  state: string;          // Required in backend
  zipCode: string;        // Required in backend
  city?: string;
  neighborhood?: string;
  address?: string;
  addressNumber?: string; // Backend uses 'addressNumber', not 'number'

  // Business information
  companyType?: CompanyType;
  companySize?: CompanySize | null;
  pcmsoExemptNr01?: boolean;
  status: CompanyStatus;

  // Extended fields (new)
  stateRegistration?: string;
  municipalRegistration?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  avatarPath?: string;
  ibgeCode?: string;
  documentType?: DocumentType;
  documentNumber?: string;
  ecacExpirationDate?: string;
  esocialSendMode?: ESocialSendMode;
  conditionStartDate?: string;
  procuracaoConfirmada?: boolean;
  procuracaoConfirmadaEm?: string | null;
  parentCompanyId?: number;

  // Modules (relational table - backend returns array of module objects)
  modules: CompanyModuleData[];

  // Sub-resources (optional - returned when requested)
  responsibles?: CompanyResponsible[];
  contacts?: CompanyContact[];
  cnaes?: CompanyCnae[];

  // Parent company (optional - returned when requested)
  parentCompany?: Company;
  branches?: Company[];

  // Link counts (subqueries of the list SELECT - active clinics, responsibles, contacts, CNAEs)
  providerCount?: number;
  responsibleCount?: number;
  contactCount?: number;
  cnaeCount?: number;

  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    departments?: number;
    occupations?: number;
    employees?: number;
    riskApplications?: number;
    sourceApplications?: number;
    examApplications?: number;
  };

  // System fields
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

// Legacy type for backwards compatibility
export type CompanyModule =
  | 'medicineSegment'
  | 'safetySegment'
  | 'environmentSegment'
  | 'eSocial';

// Company creation request (aligned with backend)
export interface CreateCompanyRequest {
  legalName: string;
  tradeName?: string;
  cnpj: string;

  state: string;          // Required
  zipCode: string;        // Required
  city?: string;
  neighborhood?: string;
  address?: string;
  addressNumber?: string;

  companyType?: CompanyType;
  companySize?: CompanySize | null;
  pcmsoExemptNr01?: boolean;
  status?: CompanyStatus;

  // Extended fields
  stateRegistration?: string;
  municipalRegistration?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  ibgeCode?: string;
  documentType?: DocumentType;
  documentNumber?: string;
  ecacExpirationDate?: string;
  esocialSendMode?: ESocialSendMode;
  conditionStartDate?: string;
  procuracaoConfirmada?: boolean;
  procuracaoConfirmadaEm?: string | null;
  parentCompanyId?: number;

  // Modules as array of module types
  modules: ModuleType[];
}

// Company update request
export interface UpdateCompanyRequest extends Partial<CreateCompanyRequest> {
  id?: never; // Prevent id from being updated
}

// Company filters for listing (aligned with backend)
export interface CompanyFilters {
  search?: string;
  status?: string;
  state?: string;
  city?: string;
  companyType?: string;
  modules?: ModuleType[];  // Array of modules to filter
  esocialSendMode?: ESocialSendMode;
  parentCompanyId?: number;
  includeDeleted?: boolean;
  createdAfter?: string;
  createdBefore?: string;
}

// Company form data (for React Hook Form) - aligned with backend
export interface CompanyFormData {
  legalName: string;
  tradeName: string;
  cnpj: string;

  state: string;
  zipCode: string;
  city: string;
  neighborhood: string;
  address: string;
  addressNumber: string;

  companyType: string;
  companySize: string;
  pcmsoExemptNr01: boolean;
  status: string;

  // Extended fields
  stateRegistration: string;
  municipalRegistration: string;
  email: string;
  phone: string;
  whatsapp: string;
  ibgeCode: string;
  documentType: string;
  documentNumber: string;
  ecacExpirationDate: string;
  esocialSendMode: string;
  conditionStartDate: string;
  parentCompanyId: string;

  // Contacts by type (for inline form)
  contacts: Partial<Record<ContactType, { name: string; email: string; phone: string }>>;

  // Modules as object with boolean values (for form switches)
  modules: {
    [ModuleType.MEDICINE]: boolean;
    [ModuleType.SAFETY]: boolean;
    [ModuleType.ENVIRONMENT]: boolean;
    [ModuleType.ESOCIAL]: boolean;
  };
}

// Company responsible form data
export interface CompanyResponsibleFormData {
  responsibleType: ResponsibleType | '';
  providerId: string;
  professionalId: string;
  startDate: string;
  endDate: string;
  notes: string;
}

// Company contact form data
export interface CompanyContactFormData {
  contactType: ContactType | '';
  name: string;
  email: string;
  phone: string;
}

// Company CNAE form data
export interface CompanyCnaeFormData {
  cnaeCode: string;
  description: string;
  isPrimary: boolean;
}

// Company validation errors
export interface CompanyValidationError {
  field: keyof CompanyFormData;
  message: string;
}

// Company stats interface (aligned with backend)
export interface CompanyStats {
  total: number;
  active: number;
  inactive: number;
  byModule: {
    [ModuleType.MEDICINE]: number;
    [ModuleType.SAFETY]: number;
    [ModuleType.ENVIRONMENT]: number;
    [ModuleType.ESOCIAL]: number;
  };
  byState: Record<string, number>;
}

// Company dashboard data
export interface CompanyDashboard {
  company: Company;
  stats: {
    employeesCount: number;
    departmentsCount: number;
    appointmentsCount: number;
    pendingExamsCount: number;
  };
}

// Company hierarchy data
export interface CompanyHierarchy {
  company: Company;
  parent?: Company;
  branches: Company[];
}

// Company summary for expandable rows
export interface CompanySummaryResponsibleItem {
  id: number;
  type: ResponsibleType;
  name: string;
}

export interface CompanySummaryContactItem {
  id: number;
  type: ContactType;
  name: string;
}

export interface CompanySummaryCnaeItem {
  id: number;
  code: string;
  description: string;
}

export interface CompanySummaryProviderItem {
  id: number;
  name: string;
  providerId: number;
  serviceType?: string | null;
}

export interface CompanySummary {
  responsibles: {
    count: number;
    items: CompanySummaryResponsibleItem[];
  };
  contacts: {
    count: number;
    items: CompanySummaryContactItem[];
  };
  cnaes: {
    count: number;
    items: CompanySummaryCnaeItem[];
  };
  providers: {
    count: number;
    items: CompanySummaryProviderItem[];
  };
}

// ==================== LABELS ====================

// Company status labels (Portuguese)
export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  [CompanyStatus.ACTIVE]: 'Ativa',
  [CompanyStatus.INACTIVE]: 'Inativa',
  [CompanyStatus.SUSPENDED]: 'Suspensa',
  [CompanyStatus.DEFAULTING]: 'Inadimplente',
  [CompanyStatus.BLOCKED]: 'Bloqueada',
};

// Legacy status labels for backwards compatibility
export const LEGACY_STATUS_LABELS = {
  ATIVA: 'Ativa',
  INATIVA: 'Inativa',
  SUSPENSA: 'Suspensa',
  INADIMPLENTE: 'Inadimplente',
  BLOQUEADA: 'Bloqueada',
} as const;

// Company type labels (Portuguese)
export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  [CompanyType.HEADQUARTERS]: 'Matriz',
  [CompanyType.BRANCH]: 'Filial',
};

// Company size labels (Portuguese); "nenhum desses" is the absence of a size
export const COMPANY_SIZE_LABELS: Record<CompanySize, string> = {
  [CompanySize.MEI]: 'MEI — Microempreendedor individual',
  [CompanySize.ME]: 'ME — Microempresa',
  [CompanySize.EPP]: 'EPP — Empresa de pequeno porte',
};

export const COMPANY_SIZE_NONE_LABEL = 'Nenhum desses';

/** A dispensa de elaborar o PCMSO pela NR-01 só é declarável por ME e EPP (NR-7 7.7.2). */
export function porteAceitaDispensa(size: string | null | undefined): boolean {
  return size === CompanySize.ME || size === CompanySize.EPP;
}

// Document type labels (Portuguese)
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  [DocumentType.CNPJ]: 'CNPJ',
  [DocumentType.CAEPF]: 'CAEPF',
  [DocumentType.CNO]: 'CNO',
};

// eSocial send mode labels (Portuguese)
export const ESOCIAL_SEND_MODE_LABELS: Record<ESocialSendMode, string> = {
  [ESocialSendMode.DONT_SEND]: 'Não Enviar',
  [ESocialSendMode.XML_ONLY]: 'Somente XML',
  [ESocialSendMode.USE_CERTIFICATE]: 'Com Certificado',
};

// Responsible type labels (Portuguese)
export const RESPONSIBLE_TYPE_LABELS: Record<ResponsibleType, string> = {
  [ResponsibleType.CLINIC_PROVIDER]: 'Prestador Clínica',
  [ResponsibleType.LAB_PROVIDER]: 'Prestador Laboratório',
  [ResponsibleType.ENVIRONMENT_PROVIDER]: 'Prestador Ambiental',
  [ResponsibleType.MEDICAL_RESPONSIBLE]: 'Responsável Médico',
  [ResponsibleType.ENVIRONMENTAL_RESPONSIBLE]: 'Responsável Ambiental',
  [ResponsibleType.TECHNICAL_RESPONSIBLE]: 'Responsável Técnico',
  [ResponsibleType.LEGAL_RESPONSIBLE]: 'Responsável Legal',
  [ResponsibleType.SPEECH_THERAPY_RESPONSIBLE]: 'Responsável Fonoaudiólogo',
};

// Contact type labels (Portuguese)
export const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  [ContactType.ADMINISTRATIVE]: 'Administrativo',
  [ContactType.ACCOUNTING]: 'Contábil',
  [ContactType.FINANCIAL]: 'Financeiro',
  [ContactType.COMMERCIAL]: 'Comercial',
  [ContactType.HR]: 'RH',
};

// Company module labels
export const MODULE_LABELS: Record<ModuleType, string> = {
  [ModuleType.MEDICINE]: 'Medicina Ocupacional',
  [ModuleType.SAFETY]: 'Segurança do Trabalho',
  [ModuleType.ENVIRONMENT]: 'Meio Ambiente',
  [ModuleType.ESOCIAL]: 'eSocial',
};

// Legacy labels for backwards compatibility
export const COMPANY_MODULE_LABELS: Record<CompanyModule, string> = {
  medicineSegment: 'Medicina Ocupacional',
  safetySegment: 'Segurança do Trabalho',
  environmentSegment: 'Meio Ambiente',
  eSocial: 'eSocial',
};

// Company type options (legacy format - for backwards compatibility)
export const COMPANY_TYPE_OPTIONS = [
  CompanyType.HEADQUARTERS,
  CompanyType.BRANCH,
] as const;

// ==================== UTILITY FUNCTIONS ====================

// Utility functions for input formatting
export const formatCNPJ = (value: string): string => {
  // Remove all non-numeric characters
  const numericOnly = value.replace(/\D/g, '');

  // Apply CNPJ mask: XX.XXX.XXX/XXXX-XX
  if (numericOnly.length <= 2) {
    return numericOnly;
  } else if (numericOnly.length <= 5) {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2)}`;
  } else if (numericOnly.length <= 8) {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2, 5)}.${numericOnly.slice(5)}`;
  } else if (numericOnly.length <= 12) {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2, 5)}.${numericOnly.slice(5, 8)}/${numericOnly.slice(8)}`;
  } else {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2, 5)}.${numericOnly.slice(5, 8)}/${numericOnly.slice(8, 12)}-${numericOnly.slice(12, 14)}`;
  }
};

export const formatCEP = (value: string): string => {
  // Remove all non-numeric characters
  const numericOnly = value.replace(/\D/g, '');

  // Apply CEP mask: XXXXX-XXX
  if (numericOnly.length <= 5) {
    return numericOnly;
  } else {
    return `${numericOnly.slice(0, 5)}-${numericOnly.slice(5, 8)}`;
  }
};

export const formatPhone = (value: string): string => {
  // Remove all non-numeric characters
  const numericOnly = value.replace(/\D/g, '');

  // Mobile: (XX) 9XXXX-XXXX
  if (numericOnly.length === 11) {
    return numericOnly.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }

  // Landline: (XX) XXXX-XXXX
  if (numericOnly.length === 10) {
    return numericOnly.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }

  // Partial formatting
  if (numericOnly.length <= 2) {
    return numericOnly.length > 0 ? `(${numericOnly}` : '';
  } else if (numericOnly.length <= 6) {
    return `(${numericOnly.slice(0, 2)}) ${numericOnly.slice(2)}`;
  } else if (numericOnly.length <= 10) {
    return `(${numericOnly.slice(0, 2)}) ${numericOnly.slice(2, 6)}-${numericOnly.slice(6)}`;
  } else {
    return `(${numericOnly.slice(0, 2)}) ${numericOnly.slice(2, 7)}-${numericOnly.slice(7, 11)}`;
  }
};

export const formatCnaeCode = (value: string): string => {
  // Remove all non-numeric characters
  const numericOnly = value.replace(/\D/g, '');

  // Apply CNAE mask: XX.XX-X/XX
  if (numericOnly.length <= 2) {
    return numericOnly;
  } else if (numericOnly.length <= 4) {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2)}`;
  } else if (numericOnly.length <= 5) {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2, 4)}-${numericOnly.slice(4)}`;
  } else {
    return `${numericOnly.slice(0, 2)}.${numericOnly.slice(2, 4)}-${numericOnly.slice(4, 5)}/${numericOnly.slice(5, 7)}`;
  }
};

export const removeCNPJMask = (cnpj: string): string => {
  return cnpj.replace(/\D/g, '');
};

export const removeCEPMask = (cep: string): string => {
  return cep.replace(/\D/g, '');
};

export const removePhoneMask = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

export const removeCnaeMask = (cnae: string): string => {
  return cnae.replace(/\D/g, '');
};

// Get status color for chips
export const getCompanyStatusColor = (status: CompanyStatus): 'success' | 'default' | 'warning' | 'error' => {
  switch (status) {
    case CompanyStatus.ACTIVE:
      return 'success';
    case CompanyStatus.INACTIVE:
      return 'default';
    case CompanyStatus.SUSPENDED:
      return 'warning';
    case CompanyStatus.DEFAULTING:
    case CompanyStatus.BLOCKED:
      return 'error';
    default:
      return 'default';
  }
};
